'use client';

import { useState } from 'react';
import { useClerk } from '@clerk/nextjs';
import { SIGN_UP_REDIRECT_PARAM } from '@lib/auth/constants/sign-up-redirect-param';
import { useSignOut } from '@lib/auth/hooks/use-signout';
import { UseGoogleSignInReturn } from '@lib/auth/types';
import { sanitizeRedirectUrl } from '@lib/auth/utils/sanitize-redirect-url';
import { isAlreadySignedInError } from '@lib/clerk/utils/is-already-signed-in-error';
import { resolveClerkErrorMessage } from '@lib/clerk/utils/resolve-clerk-error-message';
import { AUTH_ROUTES } from '@lib/constants/apis';
import { getLogger } from '@lib/observability/logging/log.client';
const {
  logInfo,
  logError
} = getLogger('lib/clerk/hooks/use-clerk-google-signin');
function getPrimaryClerkError(error: unknown): unknown {
  if (!error || typeof error !== 'object') return error;
  const {
    errors
  } = error as {
    errors?: unknown;
  };
  if (!Array.isArray(errors)) return error;
  return errors[0] ?? error;
}
function getClerkErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const {
    code
  } = error as {
    code?: unknown;
  };
  return typeof code === 'string' ? code : undefined;
}

// A Google account with no Clerk user finishes on the
// callback as a sign-up, which navigates to the
// after-sign-up URL and ignores `redirectUrlComplete`. The
// callback reads that destination from its own URL.
// Docs:
//   https://clerk.com/docs/guides/development/custom-flows/authentication/oauth-connections
function buildCallbackUrl(signUpRedirectUrl: string | undefined): string {
  if (!signUpRedirectUrl) return AUTH_ROUTES.CALLBACK_CLERK;
  const destination = sanitizeRedirectUrl(signUpRedirectUrl);
  return `${AUTH_ROUTES.CALLBACK_CLERK}?${SIGN_UP_REDIRECT_PARAM}=${encodeURIComponent(destination)}`;
}

/**
 * Google OAuth sign-in. Initiates Clerk's OAuth redirect and recovers
 * from a ghost `session_exists` once. Each SSO outcome is logged so
 * the long-standing silent dead-button is visible in production; a
 * returned error surfaces via `error`. On success the hook returns
 * and relies on Clerk's redirect — detecting a success that never
 * redirects is the caller's responsibility.
 *
 * Uses the typed SignIn resource's `authenticateWithRedirect` (not the
 * Core 3 future `signIn.sso()` helper). Production evidence for
 * PORTAL-VN shows `sso()` can return `{ error: null }` in ~10ms with
 * no Clerk/Google network call and no navigation, while this API owns
 * the hard redirect into `/auth/callback/clerk`
 * (`AuthenticateWithRedirectCallback`).
 */
export function useClerkGoogleSignIn(): UseGoogleSignInReturn {
  const clerk = useClerk();
  const signOut = useSignOut();
  const [error, setError] = useState<Error | null>(null);
  const startSso = async (redirectedFrom: string | null | undefined, signUpRedirectUrl: string | undefined, isRetry: boolean): Promise<void> => {
    const destination = sanitizeRedirectUrl(redirectedFrom);
    try {
      const signInResource = clerk.client?.signIn;
      if (!signInResource) {
        throw new Error('Google sign-in is not ready');
      }
      await signInResource.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: buildCallbackUrl(signUpRedirectUrl),
        redirectUrlComplete: destination
      });
      // Log info so we can troubleshoot dead Google button (redirect accepted; navigation expected).
      logInfo('Google SSO: accepted, redirect expected', {
        isRetry
      });
      return;
    } catch (error_0) {
      const ssoError = getPrimaryClerkError(error_0);

      // `session_exists` means Clerk still holds a session this client no
      // longer reflects — from the visitor's side they are signed out on
      // /login. Flush it and retry SSO once so the click reaches Google;
      // `isRetry` caps recovery at one attempt.
      if (isAlreadySignedInError(ssoError) && !isRetry) {
        // Log info so we can troubleshoot dead Google button (ghost session flush + retry).
        logInfo('Google SSO: session_exists, flushing and retrying once');
        await signOut(() => startSso(redirectedFrom, signUpRedirectUrl, true));
        return;
      }
      const errorObj = new Error(resolveClerkErrorMessage(ssoError as {
        message: string;
      } | null, {
        flow: 'google-sign-in',
        userInfo: {
          strategy: 'oauth_google'
        },
        fallback: 'Sign-in failed'
      }));
      // Log error so we can investigate dead Google button (SSO returned an error).
      logError('Google SSO: failed', errorObj, {
        clerkErrorCode: getClerkErrorCode(ssoError),
        isRetry
      });
      setError(errorObj);
      throw errorObj;
    }
  };
  const signInWithGoogle = async (redirectedFrom_0?: string | null, signUpRedirectUrl_0?: string) => {
    setError(null);
    await startSso(redirectedFrom_0, signUpRedirectUrl_0, false);
  };
  return {
    signInWithGoogle,
    error
  };
}